/**
 * Caldera Revieve skin-analysis quiz integration.
 *
 * Lazy-loads the Revieve SDK once per page and opens the modal whenever
 * a quiz trigger is clicked. Designed to handle multiple triggers
 * simultaneously: every trigger shares a single `Promise<RevieveSDK>`
 * created by `loadRevieveSDK()`, so concurrent clicks during SDK load
 * coalesce into one network request and one `Revieve.Init` call.
 *
 * ## Triggering the quiz
 *
 * Two ways to wire any element as a quiz trigger — both work for the
 * stock `blocks/button.liquid`, custom liquid, or hand-written HTML.
 *
 *   1. Link to `/pages/quiz` (recommended). Any `<a>` whose href ends
 *      in `/pages/quiz` (with optional querystring) opens the modal.
 *      For the button block this means setting:
 *
 *        "settings": { "link": "shopify://pages/quiz" }
 *
 *      The page also serves as a graceful fallback: if the SDK fails
 *      to load within READY_TIMEOUT_MS the click falls through to a
 *      normal navigation, so the user still lands somewhere sensible.
 *
 *   2. Add the `caldera-quiz-trigger` class to any element. Useful for
 *      buttons that should fire the modal without an anchor href.
 *
 * ## Liquid-derived values
 *
 * `window.calderaQuizConfig.shopUrl` is set inline by
 * snippets/caldera-revieve-quiz.liquid. The asset never imports Liquid
 * directly so it stays a cacheable static file.
 *
 * Ported from the legacy Caldera theme's sections/quiz.liquid.
 */

/**
 * @typedef {object} RevieveProduct
 * @property {string} url
 */

/**
 * @typedef {object} RevieveAdvisorData
 * @property {{ activeSelections?: { skin_type?: string } }} [data]
 */

/**
 * @typedef {object} RevieveAPI
 * @property {() => void} show
 * @property {() => Promise<RevieveAdvisorData>} getAdvisorData
 */

/**
 * @typedef {object} RevieveSDK
 * @property {RevieveAPI} API
 * @property {(config: RevieveConfig, ready?: () => void) => void} Init
 */

/**
 * @typedef {object} RevieveConfig
 * @property {string} partner_id
 * @property {string} experienceId
 * @property {string} locale
 * @property {string} env
 * @property {boolean} avoidCookies
 * @property {boolean} disableLauncherButton
 * @property {object} analytics
 * @property {(products: RevieveProduct | RevieveProduct[]) => Promise<void>} onAddToCart
 * @property {() => void} onClose
 * @property {() => void} onCheckout
 * @property {(product: RevieveProduct) => void} onClickProduct
 */

/**
 * @typedef {object} CalderaQuizConfig
 * @property {string} shopUrl
 */

/**
 * @typedef {Window & {
 *   Revieve?: RevieveSDK;
 *   calderaQuizConfig?: CalderaQuizConfig;
 * }} CalderaWindow
 */

const win = /** @type {CalderaWindow} */ (window);
const SHOP_URL = win.calderaQuizConfig?.shopUrl ?? '';
const REVIEVE_LOADER_URL = 'https://cdn.revieve.com/revieve-plugin-v4/revieve-plugin-loader.js';
const READY_TIMEOUT_MS = 3000;
const QUIZ_TRIGGER_CLASS = 'caldera-quiz-trigger';
const QUIZ_HREF_PATTERN = /\/pages\/quiz(\?.*)?$/;
const QUIZ_FALLBACK_PATH = '/pages/quiz';

/**
 * Cached promise that resolves once the SDK is loaded and initialised.
 * Module-scoped so every trigger click awaits the same instance.
 *
 * @type {Promise<RevieveSDK> | null}
 */
let revieveReadyPromise = null;

// =============================================================================
// Revieve callbacks
// =============================================================================

const persistSkinType = async () => {
  if (!win.Revieve?.API?.getAdvisorData) return;
  try {
    const data = await win.Revieve.API.getAdvisorData();
    const skinType = data?.data?.activeSelections?.skin_type;
    if (!skinType) return;
    try {
      localStorage.setItem('skinType', skinType);
    } catch {
      // localStorage throws in private mode — swallow.
    }
  } catch (err) {
    console.error('Revieve advisor data error:', err);
  }
};

/**
 * @param {RevieveProduct | RevieveProduct[]} products
 */
const handleAddToCart = async (products) => {
  persistSkinType();

  const list = Array.isArray(products) ? products : [products];
  /** @type {{ id: number; quantity: number }[]} */
  const items = [];

  for (const product of list) {
    try {
      const res = await fetch(`${product.url}.js`);
      if (!res.ok) {
        console.error('Failed to fetch product', product.url, res.status);
        continue;
      }
      const data = await res.json();
      const variantId = data?.variants?.[0]?.id;
      if (typeof variantId === 'number') items.push({ id: variantId, quantity: 1 });
    } catch (err) {
      console.error('Error resolving variant for product:', product, err);
    }
  }

  if (!items.length) return;

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Cart add failed: ${res.status} ${errText}`);
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } catch (err) {
    console.error('Revieve onAddToCart error:', err);
  }
};

const handleCheckout = async () => {
  if (!win.Revieve?.API?.getAdvisorData) return;
  try {
    const data = await win.Revieve.API.getAdvisorData();
    const skinType = data?.data?.activeSelections?.skin_type;
    if (skinType && ['oily', 'normal', 'dry'].includes(skinType)) {
      const url = `${SHOP_URL}/collections/all-products?filter.p.m.custom.skin_type=${skinType}&sort_by=manual`;
      window.location.replace(url);
    }
  } catch (err) {
    console.error('Revieve checkout advisor error:', err);
  }
};

/**
 * @param {RevieveProduct} product
 */
const handleClickProduct = (product) => {
  persistSkinType();
  if (product?.url) window.open(`${SHOP_URL}${product.url}`, '_blank');
};

const buildConfig = () => /** @type {RevieveConfig} */ ({
  partner_id: 'Ya3H8N8z0Z',
  experienceId: 'experience demo',
  locale: 'en',
  env: 'prod',
  avoidCookies: false,
  disableLauncherButton: true,
  analytics: {
    disableUsageAnalytics: false,
    disableProductAnalytics: false,
    disableErrorReporting: false,
  },
  onAddToCart: handleAddToCart,
  onClose: () => { persistSkinType(); },
  onCheckout: handleCheckout,
  onClickProduct: handleClickProduct,
});

// =============================================================================
// SDK loader (idempotent)
// =============================================================================

/**
 * Inject the Revieve loader script and resolve once `Revieve.Init`
 * has finished bootstrapping. Subsequent calls return the cached
 * promise so the script tag is only ever inserted once.
 *
 * @returns {Promise<RevieveSDK>}
 */
const loadRevieveSDK = () => {
  if (revieveReadyPromise) return revieveReadyPromise;

  revieveReadyPromise = new Promise((resolve, reject) => {
    if (win.Revieve?.API?.show) {
      resolve(win.Revieve);
      return;
    }

    const script = document.createElement('script');
    script.src = REVIEVE_LOADER_URL;
    script.async = true;

    script.addEventListener('load', () => {
      if (!win.Revieve) {
        reject(new Error('Revieve loader did not expose window.Revieve.'));
        return;
      }
      const sdk = win.Revieve;
      sdk.Init(buildConfig(), () => {
        document.dispatchEvent(new CustomEvent('caldera:revieve:ready'));
        resolve(sdk);
      });
    });

    script.addEventListener('error', () => {
      reject(new Error('Failed to load Revieve SDK.'));
    });

    (document.head ?? document.documentElement).appendChild(script);
  });

  // If the SDK fails to load, clear the cache so a future click can retry.
  revieveReadyPromise.catch(() => {
    revieveReadyPromise = null;
  });

  return revieveReadyPromise;
};

// =============================================================================
// Click handling
// =============================================================================

/**
 * @param {Element | null} el
 * @returns {boolean}
 */
const isQuizTrigger = (el) => {
  if (!el) return false;
  if (el.classList.contains(QUIZ_TRIGGER_CLASS)) return true;
  const href = el.getAttribute('href');
  return Boolean(href && QUIZ_HREF_PATTERN.test(href));
};

/**
 * @param {Element | null} trigger
 */
const fallbackNavigate = (trigger) => {
  if (trigger instanceof HTMLAnchorElement && trigger.href) {
    window.location.href = trigger.href;
  } else {
    window.location.href = QUIZ_FALLBACK_PATH;
  }
};

/**
 * @param {Element | null} trigger
 */
const openQuiz = async (trigger) => {
  let timer = 0;
  /** @type {Promise<never>} */
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(
      () => reject(new Error('Revieve SDK timed out')),
      READY_TIMEOUT_MS,
    );
  });

  try {
    const sdk = await Promise.race([loadRevieveSDK(), timeout]);
    sdk.API.show();
  } catch (err) {
    console.error('Caldera quiz: opening modal failed, falling back to navigation.', err);
    fallbackNavigate(trigger);
  } finally {
    window.clearTimeout(timer);
  }
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('a, button, [role="button"]');
  if (!isQuizTrigger(trigger)) return;

  event.preventDefault();
  event.stopPropagation();
  openQuiz(trigger);
}, true);

// =============================================================================
// Eager warmup
// =============================================================================

// Start fetching the SDK during browser idle time so the first click is
// instant. Errors are silenced — they'll surface again with a proper
// fallback on actual click.
const warmup = () => {
  loadRevieveSDK().catch(() => {});
};

if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(warmup, { timeout: 2000 });
} else {
  window.addEventListener('load', () => window.setTimeout(warmup, 0));
}

export {};
